import { useEffect, useState } from "react";
import { CharacterPartService } from "../../../services/CharacterPartService";
import { useSelector } from "react-redux";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { useDispatch } from "react-redux";
import {
  initializeCharacterPartLibrary,
  resetCharacterPartLibrary,
  selectPagedCharacterParts,
  updateCharacterPartLibrary,
} from "../../../app/slices/CharacterPartsSlice";
import { CharacterPartType } from "../../../dto/CharacterPartDTO";

export default function useCharacterPartLibraryPagination() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const currentOrganization = useSelector(selectCurrentOrganization);
  const pagedCharParts = useSelector(selectPagedCharacterParts);
  const isInitialized = pagedCharParts.length > 0;

  const [filter, setFilter] = useState("");
  const [type, setType] = useState(CharacterPartType.None);

  useEffect(() => {
    loadFirstPage();
  }, [currentOrganization?.id, filter, type]);

  const loadFirstPage = async () => {
    if (!currentOrganization?.id) return;

    const result = await CharacterPartService.GetCharacterPartGenderedPairs(
      currentOrganization!.id,
      page,
      pageSize,
      filter,
      type === CharacterPartType.None ? undefined : type
    );

    if (!result) return;

    dispatch(
      initializeCharacterPartLibrary({
        totalCount: result.totalCount,
        firstPage: result.charPartPairs,
      })
    );
  };

  const handleRangeChanged = (range: {
    startIndex: number;
    endIndex: number;
  }): void => {
    const { startIndex, endIndex } = range;
    const startPage = Math.floor(startIndex / pageSize);
    const endPage = Math.floor(endIndex / pageSize);

    for (let i = startPage; i <= endPage; i++) {
      if (pagedCharParts[i * pageSize]?.name === "unloaded") {
        loadPage(i);
      }
    }
  };

  const loadPage = async (page: number) => {
    if (!currentOrganization?.id) return;

    const result = await CharacterPartService.GetCharacterPartGenderedPairs(
      currentOrganization!.id,
      page + 1,
      pageSize,
      filter,
      type === CharacterPartType.None ? undefined : type
    );
    if (!result) return;
    dispatch(
      updateCharacterPartLibrary({
        pageNumber: page,
        page: result.charPartPairs,
      })
    );
  };

  const handleRefresh = async () => {
    dispatch(resetCharacterPartLibrary());
    setFilter("");
    setType(CharacterPartType.None);
    if (!currentOrganization?.id) return;
    setIsRefreshing(true);
    try {
      const result = await CharacterPartService.GetCharacterPartGenderedPairs(
        currentOrganization!.id,
        1,
        pageSize,
        "",
        undefined
      );
      if (!result) return;
      dispatch(
        initializeCharacterPartLibrary({
          totalCount: result.totalCount,
          firstPage: result.charPartPairs,
        })
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
    handleRangeChanged,
    setFilter,
    filter,
    setType,
    type,
    handleRefresh,
    isRefreshing,
  };
}
